import { inject, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { HttpClient } from "@angular/common/http";
import {
  Observable,
  of,
  tap,
  catchError,
  forkJoin,
  map,
  firstValueFrom,
} from "rxjs";
import { environment } from "src/environments/environment";

// Type definition for translation objects
type TranslationObject = Record<string, any>;

@Injectable({
  providedIn: "root",
})
export class TranslationLoaderService {
  private translateSvc = inject(TranslateService);
  private http = inject(HttpClient);

  private readonly i18nAssetVersion = environment.i18nAssetVersion ?? "1";

  // Single source of truth: stores merged translations per language
  private masterTranslations = new Map<string, TranslationObject>(); // "en" | "es" -> merged translations

  // Track which views have been loaded to avoid duplicate HTTP requests
  private loadedViews = new Set<string>(); // "{lang}-{viewName}-{assetVersion}"

  // Resolvers wait for globals before merging view JSON
  private readonly globalTranslationsReady: Promise<void>;

  constructor() {
    this.globalTranslationsReady = this.loadGlobalTranslations();
  }

  private i18nAssetUrl(relativePath: string): string {
    return `/assets/i18n/${relativePath}?v=${this.i18nAssetVersion}`;
  }

  private viewCacheKey(lang: string, viewName: string): string {
    return `${lang}-${viewName}-${this.i18nAssetVersion}`;
  }

  /**
   * Load global en/es JSON and merge into master without dropping lazy-loaded keys.
   */
  private loadGlobalTranslations(): Promise<void> {
    const langs = ["en", "es"];

    const loadObservables = langs.map((lang) =>
      this.http.get(this.i18nAssetUrl(`${lang}.json`)).pipe(
        tap((globalTranslations) => {
          this.mergeGlobalTranslationsForLang(
            lang,
            globalTranslations as TranslationObject,
          );
        }),
        catchError((error) => {
          console.warn(
            `Error loading global translations for '${lang}':`,
            error,
          );
          if (!this.masterTranslations.has(lang)) {
            this.masterTranslations.set(lang, {});
          }
          return of(null);
        }),
        map(() => void 0),
      ),
    );

    return firstValueFrom(forkJoin(loadObservables)).then(() => void 0);
  }

  private mergeGlobalTranslationsForLang(
    lang: string,
    globalTranslations: TranslationObject,
  ): void {
    const existing = this.masterTranslations.get(lang) ?? {};
    const merged = this.deepMerge(globalTranslations, existing);
    this.masterTranslations.set(lang, merged);
    this.applyMasterToTranslateService(lang);
  }

  private applyMasterToTranslateService(lang: string): void {
    const master = this.masterTranslations.get(lang);
    if (!master || Object.keys(master).length === 0) {
      return;
    }
    this.translateSvc.setTranslation(lang, master, true);
  }

  /**
   * Load view-specific translations and merge with global translations
   * This is the main method called by the resolver
   * @param viewName - Name of the view to load
   * @returns Observable<boolean> - true when translations are loaded and merged
   */
  loadAndMergeViewTranslations(viewName: string): Observable<boolean> {
    const langs = ["en", "es"];

    const loadObservables = langs.map((lang) =>
      this.loadViewTranslationForLang(viewName, lang),
    );

    return forkJoin(loadObservables).pipe(
      map(() => true),
      catchError((error) => {
        console.error(
          `Error loading translations for view '${viewName}':`,
          error,
        );
        return of(false);
      }),
    );
  }

  /**
   * Internal: Load view translations for a specific language and merge with master
   * @param viewName - Name of the view
   * @param lang - Language code (en, es, etc)
   * @returns Observable
   */
  private loadViewTranslationForLang(
    viewName: string,
    lang: string,
  ): Observable<void> {
    const cacheKey = this.viewCacheKey(lang, viewName);

    return new Observable<void>((subscriber) => {
      this.globalTranslationsReady
        .then(() => {
          if (this.loadedViews.has(cacheKey)) {
            this.applyMasterToTranslateService(lang);
            subscriber.next();
            subscriber.complete();
            return;
          }

          this.http
            .get(this.i18nAssetUrl(`${lang}/${viewName}.json`))
            .pipe(
              tap((viewTranslations) => {
                const masterForLang = this.masterTranslations.get(lang) ?? {};
                const merged = this.deepMerge(
                  masterForLang,
                  viewTranslations as TranslationObject,
                );
                this.masterTranslations.set(lang, merged);
                this.applyMasterToTranslateService(lang);
                this.loadedViews.add(cacheKey);
              }),
              catchError((error) => {
                console.error(
                  `Error loading translations for view '${viewName}' (${lang}):`,
                  error,
                );
                return of(void 0);
              }),
              map(() => void 0),
            )
            .subscribe({
              next: () => {
                subscriber.next();
                subscriber.complete();
              },
              error: (err) => subscriber.error(err),
            });
        })
        .catch((err) => subscriber.error(err));
    });
  }

  /**
   * Deep merge utility function
   * Merges source object into target, with source taking precedence
   * @param target - Target object (global translations)
   * @param source - Source object (view-specific translations)
   * @returns Merged object
   */
  private deepMerge(
    target: TranslationObject,
    source: TranslationObject,
  ): TranslationObject {
    const result = { ...target };

    for (const key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        if (
          typeof source[key] === "object" &&
          source[key] !== null &&
          !Array.isArray(source[key]) &&
          typeof result[key] === "object" &&
          result[key] !== null &&
          !Array.isArray(result[key])
        ) {
          result[key] = this.deepMerge(
            result[key] as TranslationObject,
            source[key] as TranslationObject,
          );
        } else {
          result[key] = source[key];
        }
      }
    }

    return result;
  }
}
